import React, { useState, useRef, useEffect } from 'react';
import { MesoCurriculum } from '@/services/roadmap/roadmapService';
import { DEFAULT_BELTS, Belt } from './types';
import { TimelineControls } from './TimelineControls';
import { BeltCircles } from './MesoBeltCircles';
import { TimelineTrack } from './MesoTimelineTrack';
import { BeltStripes } from './MesoBeltStripes';
import { CurriculumDialog } from '../CurriculumDialog';
import { usePermissions } from '@/contexts/unified-permissions';

interface MesoBeltTimelineProps {
  data: MesoCurriculum[];
}

export const MesoBeltTimeline: React.FC<MesoBeltTimelineProps> = ({ data }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedBelt, setSelectedBelt] = useState<Belt | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<MesoCurriculum | null>(null);
  const { user } = usePermissions();

  useEffect(() => {
    if (selectedBelt) {
      const curriculum = data.find(item => 
        item.belt_color.toLowerCase() === selectedBelt.dbColor.toLowerCase() &&
        (!selectedBelt.stripes || item.stripe_count === selectedBelt.stripes)
      );
      setSelectedCurriculum(curriculum || null);
    } else {
      setSelectedCurriculum(null);
    }
  }, [selectedBelt, data]);
  
  useEffect(() => {
    checkScrollability();
    updateContainerWidth();
    
    const handleResize = () => {
      checkScrollability();
      updateContainerWidth();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateContainerWidth = () => {
    if (!scrollContainerRef.current) return;
    setContainerWidth(scrollContainerRef.current.clientWidth);
  };

  const checkScrollability = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    setCanScrollLeft(container.scrollLeft > 2);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 5);
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -250, behavior: 'smooth' });
    setTimeout(checkScrollability, 300);
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 250, behavior: 'smooth' });
    setTimeout(checkScrollability, 300);
  };

  const handleBeltClick = (belt: Belt) => {
    if (selectedBelt && selectedBelt.name === belt.name && 
        selectedBelt.stripes === belt.stripes) {
      setSelectedBelt(null);
    } else {
      setSelectedBelt(belt);
    }
  };

  const handleCloseDialog = () => {
    setSelectedBelt(null);
  };

  const belts = DEFAULT_BELTS;

  // --- BEGIN: auto-scroll logic for Meso Roadmap
  useEffect(() => {
    if (!scrollContainerRef.current || !user) return;

    const userBeltRank = user.user_metadata?.belt_rank || "white";
    // Only auto-scroll if not white belt with 0 stripes
    if (userBeltRank.toLowerCase() === "white") {
      const stripes = user.user_metadata?.stripes ?? "0";
      if (stripes === 0 || stripes === "0" || stripes === "" || stripes === "no stripes") {
        return;
      }
    }
    // Find index for user's belt
    const userBeltIdx = belts.findIndex(
      (belt) => belt.dbColor.toLowerCase() === userBeltRank.toLowerCase()
    );
    if (userBeltIdx === -1) {
      return;
    }

    // Meso timeline: spacing and startX must match stripes/circles logic
    const startX = 40;
    const beltSpacing = 230; // Keep in sync with circles & stripes
    const beltPos = startX + userBeltIdx * beltSpacing;

    // Center the user's belt if possible
    const spaceToCenter = containerWidth / 2;
    let scrollTo = beltPos - spaceToCenter;
    const container = scrollContainerRef.current;
    const maxScroll = container.scrollWidth - containerWidth;
    scrollTo = Math.max(0, Math.min(maxScroll, scrollTo));

    container.scrollTo({ left: scrollTo, behavior: 'smooth' });
  }, [containerWidth, user, belts]);
  // --- END: auto-scroll logic

  return (
    <div className="relative w-full">
      <TimelineControls 
        onScrollLeft={scrollLeft}
        onScrollRight={scrollRight}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />
      
      <div 
        className="overflow-x-auto pb-4 hide-scrollbar w-full mx-auto"
        ref={scrollContainerRef} 
        onScroll={checkScrollability}
        style={{ 
          paddingLeft: '4px',
          paddingRight: '12px',
          scrollbarWidth: 'none',
          scrollPaddingLeft: '0px',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="relative h-[110px]" style={{ 
          width: '100%', 
          minWidth: '500px'
        }}>
          <TimelineTrack belts={belts} />
          <BeltCircles belts={belts} onBeltClick={handleBeltClick} />
          <BeltStripes belts={belts} onStripeClick={handleBeltClick} />
        </div>
      </div>
      
      <CurriculumDialog belt={selectedBelt} curriculum={selectedCurriculum} onClose={handleCloseDialog} />
    </div>
  );
};
